import { AppError } from "@artisancode/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { InstallButton } from "@/components/shared/install-button";
import { WhatsAppIcon } from "@/components/shared/whatsapp-icon";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { useLogin } from "@/hooks/use-auth";
import { getErrorMessage } from "@/lib/error-message";
import logoPattern from "@/logo-pattern.png";
import logoImg from "@/logo.webp";

import { LoginFields } from "./login-fields";
import { schema, emptyValues, type FormValues } from "./schema";
import { WhatsappLoginPanel } from "./whatsapp-login-panel";

export function Login() {
  const navigate = useNavigate();
  const { mutate: doLogin, isPending } = useLogin();
  const [useWhatsapp, setUseWhatsapp] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  function onSubmit(values: FormValues) {
    doLogin(values, {
      onSuccess: () => navigate("/dashboard"),
      onError: (error) => {
        if (error instanceof AppError) {
          if ((error.httpCode ?? 0) >= 500) {
            toast.error("Terjadi kesalahan pada server. Coba lagi nanti.");
          } else if (error.httpCode === 401) {
            toast.error("Email atau password salah.");
          } else {
            toast.error(getErrorMessage(error, "Email atau password salah."));
          }
        } else {
          toast.error(
            error instanceof Error
              ? error.message
              : "Gagal masuk. Coba lagi nanti.",
          );
        }
      },
    });
  }

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center gap-4 bg-background p-4 overflow-hidden">
      {/* JAM logo monogram pattern */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-repeat bg-center opacity-15 dark:opacity-15"
        style={{
          backgroundImage: `url(${logoPattern})`,
          backgroundSize: "260px auto",
        }}
      />
      {/* Subtle overlay gradient */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/90"
      />

      <Card className="relative z-10 w-full max-w-sm shadow-xl bg-card/95 backdrop-blur-md border-border/80">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-1">
            <img
              src={logoImg}
              alt="JAM Coffee"
              className="h-20 w-auto object-contain"
            />
          </div>
          <CardTitle className="sr-only">JAM Coffee</CardTitle>
          <CardDescription>Masuk ke akun Anda</CardDescription>
        </CardHeader>
        <CardContent>
          {useWhatsapp ? (
            <WhatsappLoginPanel onBack={() => setUseWhatsapp(false)} />
          ) : (
            <>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="grid gap-4"
                >
                  <LoginFields control={form.control} />
                  <Button
                    type="submit"
                    className="w-full mt-1"
                    disabled={isPending}
                  >
                    {isPending ? "Memproses..." : "Masuk"}
                  </Button>
                </form>
              </Form>
              <div className="my-4 flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">atau</span>
                <Separator className="flex-1" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
                onClick={() => setUseWhatsapp(true)}
              >
                <WhatsAppIcon className="h-4 w-4 text-[#25D366] shrink-0" />
                <span>Masuk dengan WhatsApp</span>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      <div className="relative z-10">
        <InstallButton />
      </div>
    </div>
  );
}
